import { alpha, HomeSectionTitle, HomeLink } from "./homeUi";
import { HomeIcon } from "./homeIcons";

/**
 * What a membership shows before its creator has saved tiles of their own —
 * identical to HOME_LINKS_DEFAULT in the admin's SettingsController, so the
 * editor opens on exactly what members already see.
 */
export const DEFAULT_HOME_LINKS = [
  { title: "Courses", subtitle: "Pick up where you left off", icon: "book", type: "menu", target: "courses" },
  { title: "Community", subtitle: "Say hi, ask anything", icon: "chat", type: "menu", target: "groups" },
  { title: "Events", subtitle: "Live sessions & workshops", icon: "calendar", type: "menu", target: "events" },
  { title: "Members", subtitle: "Find your people", icon: "users", type: "menu", target: "members" },
];

// Section → the community.menus flag that hides it.
export const SECTION_MENU = {
  courses: "course",
  groups: "community",
  events: "event",
  members: "member",
  map: "map",
  leaderboard: "leaderboard",
  rewards: "reward",
  memberchat: "memberchat",
};

const GRID = {
  1: "grid-cols-1 sm:grid-cols-2",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4",
};

/** Only http(s) — a stored `javascript:` link must never become an href. */
const safeUrl = (u) => (/^https?:\/\//i.test(String(u || "").trim()) ? String(u).trim() : null);

export default function HomeQuickLinks({ quick, menuOn, primary, onNavigate }) {
  const links = (quick?.links ?? DEFAULT_HOME_LINKS).filter((l) =>
    l.type === "url" ? !!safeUrl(l.target) : !!SECTION_MENU[l.target] && menuOn(SECTION_MENU[l.target])
  );
  if (!links.length) return null;
  const title = (quick?.title || "").trim() || "Start here";
  const grid = GRID[Math.min(links.length, 4)] || GRID[4];

  return (
    <section aria-label={title}>
      <HomeSectionTitle
        title={title}
        primary={primary}
        action={menuOn("community") ? <HomeLink primary={primary} onClick={() => onNavigate("groups")}>Go to community</HomeLink> : null}
      />
      <div className={`grid gap-3.5 ${grid}`}>
        {links.map((l, i) => {
          const inner = (
            <>
              <span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-transform duration-200 group-hover:scale-[1.06]"
                style={{ backgroundColor: alpha(primary, 0.12), color: primary }}
              >
                <HomeIcon name={l.icon} className="h-[22px] w-[22px]" />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[14.5px] font-bold text-gray-900">{l.title}</span>
                {l.subtitle ? <span className="block truncate text-[12.5px] text-gray-500">{l.subtitle}</span> : null}
              </span>
              <svg className="h-4 w-4 shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-[color:var(--tile)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                {l.type === "url" && l.new_tab ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                )}
              </svg>
            </>
          );
          const cls =
            "group flex min-h-[76px] items-center gap-3.5 rounded-2xl border border-gray-200 bg-surface px-4 py-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[color:var(--tile-b)] hover:shadow-[0_14px_28px_-18px_var(--tile)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tile)]";
          const style = { "--tile": primary, "--tile-b": alpha(primary, 0.45) };

          return l.type === "url" ? (
            <a
              key={i}
              href={safeUrl(l.target)}
              target={l.new_tab ? "_blank" : undefined}
              rel={l.new_tab ? "noopener noreferrer" : undefined}
              className={cls}
              style={style}
            >
              {inner}
            </a>
          ) : (
            <button key={i} type="button" onClick={() => onNavigate(l.target)} className={cls} style={style}>
              {inner}
            </button>
          );
        })}
      </div>
    </section>
  );
}
